import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { FileDown, RefreshCw, ArrowRightLeft, Check, Send } from "lucide-react";
import { rupiah, tanggal, angka } from "@/lib/format";

export async function openPdf(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(res.data);
    window.open(blobUrl, "_blank");
  } catch {
    toast.error("Gagal membuka dokumen");
  }
}

export default function Proforma() {
  const { data, loading, reload } = useFetch("/proformas");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  const openDetail = async (row) => {
    const res = await api.get(`/proformas/${row.id}`);
    setSel(res.data);
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); reload(); if (sel) { const r = await api.get(`/proformas/${sel.id}`); setSel(r.data); } }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const setStatus = (s) => act(() => api.post(`/proformas/${sel.id}/status`, { status: s }), `Status: ${s}`);
  const recalc = () => act(() => api.post(`/proformas/${sel.id}/recalculate`), "Dihitung ulang");
  const convert = () => act(async () => { const r = await api.post(`/proformas/${sel.id}/convert`); toast.success(`Invoice ${r.data.number} dibuat`); }, "Dikonversi ke Invoice");

  return (
    <div>
      <PageHeader title="Proforma Invoice" subtitle="Format nomor PRO/YYYY/MM/0001 — watermark PROFORMA" />
      {loading ? <div className="h-64 rounded-md bg-muted animate-pulse" /> : (
        <DataTable testId="proformas" data={data || []} onRowClick={openDetail}
          columns={[
            { key: "number", label: "No. Proforma" },
            { key: "date", label: "Tanggal", render: (r) => tanggal(r.date) },
            { key: "billing_start", label: "Periode", render: (r) => `${tanggal(r.billing_start)} – ${tanggal(r.billing_end)}` },
            { key: "grand_total", label: "Grand Total", numeric: true, render: (r) => rupiah(r.grand_total) },
            { key: "data_changed", label: "", render: (r) => r.data_changed ? <StatusBadge status="Perlu Revisi" /> : null },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ]} />
      )}

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto scrollbar-thin">
          {sel && (
            <>
              <SheetHeader><SheetTitle className="font-display flex items-center gap-3">{sel.number} <StatusBadge status={sel.status} /></SheetTitle></SheetHeader>
              <BillingDetail doc={sel} />
              {sel.data_changed && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 my-3">Data material berubah. Klik "Hitung Ulang" untuk memperbarui.</p>}
              <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => openPdf(`/proformas/${sel.id}/pdf`)} data-testid="proforma-pdf-btn"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
                {["Draft", "Perlu Revisi"].includes(sel.status) && <Button variant="outline" size="sm" onClick={recalc} disabled={busy} data-testid="proforma-recalc-btn"><RefreshCw className="h-4 w-4 mr-1" /> Hitung Ulang</Button>}
                {sel.status === "Draft" && <Button variant="outline" size="sm" onClick={() => setStatus("Menunggu Persetujuan Internal")} disabled={busy} data-testid="proforma-submit-btn"><Send className="h-4 w-4 mr-1" /> Ajukan</Button>}
                {sel.status === "Menunggu Persetujuan Internal" && <Button variant="outline" size="sm" onClick={() => setStatus("Disetujui Klien")} disabled={busy} data-testid="proforma-approve-btn"><Check className="h-4 w-4 mr-1" /> Setujui</Button>}
                {["Disetujui Klien", "Siap Dikirim", "Terkirim", "Menunggu Persetujuan Internal"].includes(sel.status) && !sel.converted_invoice_id && <Button size="sm" onClick={convert} disabled={busy} data-testid="proforma-convert-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]"><ArrowRightLeft className="h-4 w-4 mr-1" /> Konversi ke Invoice</Button>}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function BillingDetail({ doc }) {
  return (
    <div className="py-4 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <Info label="Periode" value={`${tanggal(doc.billing_start)} – ${tanggal(doc.billing_end)}`} />
        <Info label="No. PO" value={doc.po_numbers || "-"} />
        <Info label="Jatuh Tempo" value={tanggal(doc.due_date)} />
      </div>
      {doc.warnings?.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 space-y-1">
          {doc.warnings.map((w, i) => <div key={i}>• {w}</div>)}
        </div>
      )}
      <div className="rounded-md border overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/40 border-b text-xs">{["Material", "Status", "Qty", "Periode", "Hari", "Tarif", "Subtotal"].map((h) => <th key={h} className={`px-3 py-2 ${["Qty", "Hari", "Tarif", "Subtotal"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
          <tbody>{(doc.items || []).map((l, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="px-3 py-2 font-medium">{l.material_name}<div className="text-[11px] text-muted-foreground">SJ {l.dn_number || "-"}{l.rn_number ? ` · SP ${l.rn_number}` : ""}</div></td>
              <td className="px-3 py-2"><StatusBadge status={l.material_status} /></td>
              <td className="px-3 py-2 text-right tabular-nums">{angka(l.quantity)}</td>
              <td className="px-3 py-2 text-xs">{tanggal(l.rental_start)}<br />{tanggal(l.rental_end)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{l.number_of_days}</td>
              <td className="px-3 py-2 text-right tabular-nums">{rupiah(l.rate)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-medium">{rupiah(l.subtotal)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <div className="w-64 space-y-1 text-sm">
          <Line label="Subtotal" value={rupiah(doc.subtotal)} />
          <Line label="Diskon" value={rupiah(doc.discount || 0)} />
          <Line label={`PPN (${doc.tax_percent}%)`} value={rupiah(doc.tax_amount)} />
          <div className="flex justify-between pt-2 border-t font-bold"><span>Grand Total</span><span className="tabular-nums text-[#9333EA]">{rupiah(doc.grand_total)}</span></div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) { return <div><div className="text-xs text-muted-foreground">{label}</div><div className="font-medium">{value}</div></div>; }
function Line({ label, value }) { return <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className="tabular-nums">{value}</span></div>; }
